import {
  Component,
  computed,
  ElementRef,
  input,
  OnDestroy,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgIcon } from '@ng-icons/core';
import { ButtonDirective } from '../../../../components/button/button.directive';
import { AudioRecorderComponent } from '../audio-recorder/ticket-audio-recorder.component';
import { handleTicketClipboardImagePaste } from '../../domain/ticket-clipboard-images';
import {
  mergeTicketFileSelection,
  TICKET_MESSAGE_FILE_ACCEPT,
  TicketFileSelectionError,
} from '../../domain/ticket-file-selection';
import { TicketImagePreviewCache } from '../../domain/ticket-image-preview-cache';
import { ticketAttachmentKind } from '../../domain/ticket-attachment-types';
import {
  MAX_TICKET_MESSAGE_LENGTH,
  TicketReplyPayload,
} from '../../domain/ticket.models';
import { TicketAttachmentPreviewComponent } from '../attachment-preview/ticket-attachment-preview.component';
import {
  TicketSelectedAttachmentEvent,
  TicketSelectedUploadsComponent,
} from '../selected-uploads/ticket-selected-uploads.component';

export interface TicketReplyComposerLabels {
  readonly placeholder: string;
  readonly attachFiles: string;
  readonly attachmentLimits: string;
  readonly send: string;
  readonly sending: string;
  readonly previewAttachment: string;
  readonly removeFile: string;
  readonly download: string;
  readonly closePreview: string;
  readonly loadingPreview: string;
  readonly spreadsheetSheet: string;
  readonly spreadsheetEmptySheet: string;
  readonly spreadsheetPreviewTruncated: string;
}

const DEFAULT_LABELS: TicketReplyComposerLabels = {
  placeholder: 'Write a reply…',
  attachFiles: 'Attach files',
  attachmentLimits: 'Up to 5 images, PDFs or spreadsheets, 1 audio and 1 video; 10 MB each',
  send: 'Send',
  sending: 'Sending…',
  previewAttachment: 'Preview attachment',
  removeFile: 'Remove',
  download: 'Download',
  closePreview: 'Close preview',
  loadingPreview: 'Loading preview…',
  spreadsheetSheet: 'Sheet',
  spreadsheetEmptySheet: 'This sheet is empty.',
  spreadsheetPreviewTruncated: 'Preview limited to the first 500 rows and 50 columns. Download the file to view all data.',
};

@Component({
  selector: 'app-ticket-reply-composer',
  standalone: true,
  imports: [
    FormsModule,
    NgIcon,
    ButtonDirective,
    AudioRecorderComponent,
    TicketAttachmentPreviewComponent,
    TicketSelectedUploadsComponent,
  ],
  templateUrl: './ticket-reply-composer.component.html',
  styleUrl: './ticket-reply-composer.component.scss',
})
export class TicketReplyComposerComponent implements OnDestroy {
  readonly disabled = input(false);
  readonly sending = input(false);
  readonly labels = input<TicketReplyComposerLabels>(DEFAULT_LABELS);
  readonly submitted = output<TicketReplyPayload>();
  readonly validationError = output<TicketFileSelectionError>();
  readonly previewFailed = output<void>();

  @ViewChild('replyInput') private replyInput?: ElementRef<HTMLTextAreaElement>;
  @ViewChild(TicketAttachmentPreviewComponent)
  private attachmentViewer?: TicketAttachmentPreviewComponent;

  readonly attachments = signal<File[]>([]);
  readonly audio = signal<File | null>(null);
  readonly audioRecorded = signal(false);
  readonly video = signal<File | null>(null);
  readonly text = signal('');
  readonly maxMessageLength = MAX_TICKET_MESSAGE_LENGTH;
  readonly messageFileAccept = TICKET_MESSAGE_FILE_ACCEPT;
  readonly canSend = computed(
    () =>
      !this.disabled() &&
      !this.sending() &&
      this.text().length <= this.maxMessageLength &&
      (!!this.text().trim() ||
        this.attachments().length > 0 ||
        !!this.audio() ||
        !!this.video()),
  );

  private readonly imagePreviews = new TicketImagePreviewCache();
  readonly previewImageUrl = (file: File): string | null =>
    this.imagePreviews.urlFor(file);

  selectFiles(event: Event): void {
    const input = event.target as HTMLInputElement;
    const selected = Array.from(input.files ?? []);
    input.value = '';
    this.applyFileSelection(selected);
  }
  onPaste(event: ClipboardEvent): void {
    handleTicketClipboardImagePaste(event, (images) =>
      this.applyFileSelection(images),
    );
  }
  onKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' || event.shiftKey || event.isComposing) return;
    event.preventDefault();
    this.submit();
  }
  submit(): void {
    if (!this.canSend()) return;
    this.submitted.emit({
      text: this.text().trim() || undefined,
      attachments: this.attachments(),
      audio: this.audio(),
      video: this.video(),
    });
  }
  setRecordedAudio(file: File | null): void {
    this.audio.set(file);
    this.audioRecorded.set(!!file);
    if (file) this.focus();
  }
  clearAudio(): void {
    this.audio.set(null);
    this.audioRecorded.set(false);
  }
  clearVideo(): void {
    this.video.set(null);
  }
  removeAttachment(index: number): void {
    const attachments = this.attachments().filter(
      (_, current) => current !== index,
    );
    this.attachments.set(attachments);
    this.imagePreviews.sync(attachments);
  }
  previewUrl(file: File): string | null {
    return this.imagePreviews.urlFor(file);
  }
  previewImage(file: File, event: Event): void {
    const url = this.imagePreviews.urlFor(file);
    if (!url) return;
    this.attachmentViewer?.openImage(
      file.name,
      url,
      event.currentTarget instanceof HTMLElement
        ? event.currentTarget
        : undefined,
    );
  }
  async openSelectedAttachment(event: TicketSelectedAttachmentEvent): Promise<void> {
    if (ticketAttachmentKind(event.file.name, event.file.type) === 'spreadsheet') {
      await this.attachmentViewer?.openSpreadsheetFile(event.file, event.trigger);
      return;
    }
    const url = this.imagePreviews.urlFor(event.file);
    if (!url) return;
    this.attachmentViewer?.openImage(event.file.name, url, event.trigger);
  }
  imageLoadFailed(file: File): void {
    this.imagePreviews.markFailed(file);
  }
  formatFileSize(file: File): string {
    if (file.size < 1024 * 1024) {
      return `${Math.max(1, Math.round(file.size / 1024))} KB`;
    }
    return `${(file.size / (1024 * 1024)).toFixed(1)} MB`;
  }
  reset(): void {
    this.attachmentViewer?.close(false);
    this.text.set('');
    this.attachments.set([]);
    this.imagePreviews.clear();
    this.clearAudio();
    this.clearVideo();
  }
  focus(): void {
    setTimeout(() => this.replyInput?.nativeElement.focus());
  }
  ngOnDestroy(): void {
    this.attachmentViewer?.close(false);
    this.imagePreviews.clear();
  }
  private applyFileSelection(selected: File[]): void {
    if (!selected.length) return;
    const currentAudio = this.audio();
    const result = mergeTicketFileSelection(
      {
        attachments: this.attachments(),
        audio: currentAudio,
        video: this.video(),
      },
      selected,
    );
    if (result.error) {
      this.validationError.emit(result.error);
      return;
    }
    if (!result.files) return;
    this.attachments.set(result.files.attachments);
    this.imagePreviews.sync(result.files.attachments);
    this.audio.set(result.files.audio);
    this.video.set(result.files.video);
    if (result.files.audio !== currentAudio) this.audioRecorded.set(false);
    this.focus();
  }
}
